<script>
    // @ts-nocheck
    /**
     * Wunsch-Zustelldatum Picker für Payment-Step.
     *
     * Sichtbar nur wenn:
     *   - selected shipping set.useShippingDate === true (oxdeliveryset.ys_use_shipping_date)
     *   - selected payment.useShippingDate === true     (oxpayments.ys_use_shipping_date)
     *
     * Min-Datum = computeDeliveryDate('goexpress', maxDeliveryDays) → frühestmöglicher
     * Express-Termin (Legacy: ViewConfig::getMinDeliveryDate('goexpress', offset)).
     * Max-Datum = heute + 3 Monate (Legacy: ViewConfig::getMaxDeliveryDate).
     *
     * Validation client-side mirrors die legacy edv() function — kein Sonntag,
     * kein Feiertag, kein Samstag (außer saturdayDelivery für goexpress) und nicht
     * vor min. Bei valid POST → paymentStore.setDeliveryDate(date) speichert in
     * OXID-Session (sDeliveryDate) via DynamicPaymentController.
     */
    import {t} from '$utils/lang.js';
    import {paymentStore, setDeliveryDate} from '$lib/paymentStore.svelte.js';
    import {store as basketStore} from '$lib/basketStore.svelte.js';
    import {computeDeliveryDate} from '$utils/deliveryDate.js';

    const selectedShipping = $derived(
        (paymentStore.shippingMethods ?? []).find(s => s.shippingSet?.id === paymentStore.selectedShippingId)
    );
    const selectedPayment = $derived(
        (paymentStore.paymentMethods ?? []).find(p => p.id === paymentStore.selectedPaymentId)
    );

    const shippingUsesDate = $derived(Boolean(selectedShipping?.shippingSet?.useShippingDate));
    const paymentUsesDate = $derived(Boolean(selectedPayment?.useShippingDate));
    const visible = $derived(shippingUsesDate && paymentUsesDate);

    const maxOffset = $derived(basketStore.maxDeliveryDays ?? 0);

    function pad(n) { return n < 10 ? '0' + n : String(n); }
    function isoOf(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

    const minIso = $derived.by(() => {
        const r = computeDeliveryDate('goexpress', maxOffset, new Date());
        return r ? isoOf(r.deliveryDate) : '';
    });
    const maxIso = $derived.by(() => {
        const d = new Date();
        d.setMonth(d.getMonth() + 3);
        return isoOf(d);
    });

    // Lokaler State spiegelt Server-Wert + behält User-Eingaben bevor validate.
    let inputValue = $state('');
    let errorKey = $state('');
    let saving = $state(false);
    let inputEl = $state(null);

    // Sync server → lokal beim Render und wenn der Wert sich ändert
    $effect(() => {
        const remote = paymentStore.selectedDeliveryDate ?? '';
        if (remote !== inputValue && !saving) {
            inputValue = remote;
        }
    });

    // Wenn der Picker unsichtbar wird → server-seitig Datum löschen
    $effect(() => {
        if (!visible && paymentStore.selectedDeliveryDate) {
            void setDeliveryDate(null);
            inputValue = '';
            errorKey = '';
        }
    });

    /**
     * Mirrors ViewConfig::validateDeliveryDate logic in JS:
     * - Datum >= min
     * - Datum ist kein Sonntag (getDay===0)
     * - Datum ist kein Feiertag (window.deliveryInfo.holidays)
     * - Datum ist kein Samstag (außer goexpress saturdayDelivery)
     */
    function validate(iso) {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return 'invalid';
        const min = new Date(minIso);
        if (d < min) return 'too_early';
        if (d.getDay() === 0) return 'sunday';
        const info = (typeof window !== 'undefined' ? window.deliveryInfo : null) ?? {};
        if (info.holidays && info.holidays[iso]) return 'holiday';
        const saturdayOk = !!info?.types?.goexpress?.saturdayDelivery;
        if (d.getDay() === 6 && !saturdayOk) return 'saturday';
        return '';
    }

    async function handleChange(ev) {
        const iso = ev.target.value;
        inputValue = iso;
        errorKey = validate(iso);
        if (errorKey) return;
        try {
            saving = true;
            await setDeliveryDate(iso);
        } catch (e) {
            errorKey = 'save_failed';
        } finally {
            saving = false;
        }
    }

    async function handleClear() {
        try {
            saving = true;
            await setDeliveryDate(null);
            inputValue = '';
            errorKey = '';
            // WKWebView (cmux Agent-Browser) zeigt nach value='' den heutigen
            // Tag als Ghost-Default. Hard-Reset via DOM-Manipulation.
            if (inputEl) inputEl.value = '';
        } finally {
            saving = false;
        }
    }

    const errorMessage = $derived.by(() => {
        switch (errorKey) {
            case 'too_early':  return t('Frühestes mögliches Lieferdatum überschritten — bitte späteres Datum wählen.');
            case 'sunday':     return t('Sonntags ist keine Zustellung möglich.');
            case 'saturday':   return t('Samstags ist keine Zustellung möglich.');
            case 'holiday':    return t('An Feiertagen ist keine Zustellung möglich.');
            case 'save_failed':return t('Wunsch-Zustelldatum konnte nicht gespeichert werden. Bitte versuche es erneut.');
            case 'invalid':    return t('Ungültiges Datum.');
            default: return '';
        }
    });
</script>

{#if visible}
    <section class="bg-white border border-gray-102 rounded-md p-4 flex flex-col gap-3"
             data-checkout-section="delivery-date">
        <div class="flex items-start gap-3">
            <div class="shrink-0 size-9 rounded-full bg-cyprus text-white flex items-center justify-center">
                <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="5" width="18" height="16" rx="2"/>
                    <path d="M3 9h18M8 3v4M16 3v4"/>
                </svg>
            </div>
            <div class="flex flex-col">
                <h3 class="font-bold text-base text-cyprus">{t('Bitte wähle Dein Zustelldatum')}</h3>
                <p class="text-xs text-gray-103">
                    {t('Du kannst hier ein fixes Zustelldatum angeben. Die Zustellung erfolgt am gewählten Datum zwischen 8 und 15 Uhr morgens. Wenn du kein Datum angibst, kontaktieren wir dich vor Versand und machen ein Zustelldatum mit dir aus.')}
                </p>
            </div>
        </div>

        <label class="flex items-center gap-3">
            <input type="date"
                   bind:this={inputEl}
                   class="border border-gray-102 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-elm"
                   class:border-scarlet={errorKey}
                   min={minIso}
                   max={maxIso}
                   value={inputValue}
                   disabled={saving}
                   data-checkout-action="delivery-date-input"
                   onchange={handleChange}/>
            <span class="text-xs text-gray-103">{t('8-15 Uhr')}</span>
        </label>

        {#if errorMessage}
            <p class="text-xs text-scarlet" data-checkout-section="delivery-date-error">{errorMessage}</p>
        {/if}

        {#if inputValue && !errorKey}
            <div class="flex items-center justify-between gap-3">
                <span class="text-xs text-green font-bold" data-checkout-section="delivery-date-confirmed">
                    {t('Wunschtermin gespeichert')}
                </span>
                <button type="button"
                        class="text-xs text-gray-103 underline cursor-pointer"
                        disabled={saving}
                        data-checkout-action="delivery-date-clear"
                        onclick={handleClear}>
                    {t('Termin entfernen')}
                </button>
            </div>
        {/if}
    </section>
{/if}
